import dayjs from "dayjs";

/**
 * Split events that cross midnight into separate entries for each day
 * This ensures events spanning multiple days are properly rendered on each day's bar/row
 * @param {Array} events - Array of event objects from API
 * @returns {Array} - Array with midnight-crossing events split into separate entries
 */
export const splitMidnightCrossingEvents = (events) => {
  if (!events || !Array.isArray(events)) {
    return [];
  }

  const result = [];

  events.forEach((event) => {
    const startTime = dayjs(event.startTime, "YYYY-MM-DD HH:mm:ss");
    const endTime = dayjs(event.endTime, "YYYY-MM-DD HH:mm:ss");

    // Check if event crosses midnight (different dates)
    if (!startTime.isSame(endTime, "day")) {
      // Calculate total duration for proportional splitting of metrics
      const totalDurationMs = endTime.diff(startTime);

      // Split into multiple day segments
      let currentStart = startTime;

      while (!currentStart.isSame(endTime, "day")) {
        // End of current day (23:59:59.999)
        const endOfDay = currentStart.endOf("day");
        const segmentEnd = endOfDay.format("YYYY-MM-DD HH:mm:ss");
        const segmentDurationMs = endOfDay.diff(currentStart);
        const proportion = segmentDurationMs / totalDurationMs;

        // Create segment for current day (startTime to end of day)
        result.push({
          ...event,
          startTime: currentStart.format("YYYY-MM-DD HH:mm:ss"),
          endTime: segmentEnd,
          durationMinutes: Math.round(event.durationMinutes * proportion),
          // Proportionally split energy/distance values
          chargeEnergyConsumed: event.chargeEnergyConsumed * proportion,
          dischargeEnergyConsumed: event.dischargeEnergyConsumed * proportion,
          distance: event.distance * proportion,
          regenEnergy: event.regenEnergy * proportion,
          // SOC: first segment keeps startSoc, interpolate endSoc
          startSoc: currentStart.isSame(startTime) ? event.startSoc : null,
          endSoc: null, // Intermediate segments don't have accurate endSoc
          // kwhPerSoc/kmPerSoc cannot be calculated without both SOC endpoints
          kwhPerSoc: null,
          kmPerSoc: null,
          _isSplitSegment: true,
        });

        // Move to start of next day
        currentStart = currentStart.add(1, "day").startOf("day");
      }

      // Final segment: start of last day to endTime
      const finalSegmentDurationMs = endTime.diff(currentStart);
      const finalProportion = finalSegmentDurationMs / totalDurationMs;

      result.push({
        ...event,
        startTime: currentStart.format("YYYY-MM-DD HH:mm:ss"),
        endTime: event.endTime,
        durationMinutes: Math.round(event.durationMinutes * finalProportion),
        chargeEnergyConsumed: event.chargeEnergyConsumed * finalProportion,
        dischargeEnergyConsumed: event.dischargeEnergyConsumed * finalProportion,
        distance: event.distance * finalProportion,
        regenEnergy: event.regenEnergy * finalProportion,
        startSoc: null, // Final segment doesn't have accurate startSoc
        endSoc: event.endSoc,
        // kwhPerSoc/kmPerSoc cannot be calculated without both SOC endpoints
        kwhPerSoc: null,
        kmPerSoc: null,
        _isSplitSegment: true,
      });
    } else {
      // Event doesn't cross midnight, keep as-is
      result.push(event);
    }
  });

  return result;
};

/**
 * Groups flat event data by date and transforms it into the table format
 * @param {Array} flatData - Array of flat event objects from API
 * @returns {Array} - Grouped data structure for the table
 */
export const groupEventDetailsData = (flatData) => {
  if (!flatData || !Array.isArray(flatData)) {
    return [];
  }

  // Helper function to format date for grouping (data is already in correct timezone from backend)
  const formatDateGroup = (dateString) => {
    // Parse the date directly without timezone conversion and format as "D MMM YYYY"
    return dayjs(dateString, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY");
  };

  // Helper function to capitalize first letter
  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Group events by date
  const groupedByDate = flatData.reduce((acc, event) => {
    const dateGroup = formatDateGroup(event.startTime);
    if (!acc[dateGroup]) {
      acc[dateGroup] = [];
    }
    acc[dateGroup].push(event);
    return acc;
  }, {});

  // Sort dates in descending order (most recent first)
  const sortedDateGroups = Object.entries(groupedByDate).sort(
    ([dateA], [dateB]) => {
      const parseDate = (dateStr) => {
        const parts = dateStr.split(" ");
        return new Date(`${parts[1]} ${parts[0]}, ${parts[2]}`);
      };
      return parseDate(dateB) - parseDate(dateA);
    }
  );

  // Transform grouped data into table format
  const result = sortedDateGroups.map(([dateGroup, events]) => {
    // Sort events within each day chronologically (earliest first)
    const sortedEvents = events.sort((a, b) => {
      return new Date(a.startTime) - new Date(b.startTime);
    });

    const children = sortedEvents.map((event, eventIndex) => {
      const eventType = capitalize(event.eventType);

      // Base event object with camelCase keys matching column dataIndex
      const transformedEvent = {
        event_group:
          eventType === "Charge"
            ? "Charging"
            : eventType === "Discharge"
            ? "Discharging"
            : eventType,
        eventType: event.eventType,
        startTime: event.startTime,
        endTime: event.endTime,
        chargeType: event.chargeType || "-",
        durationMinutes: event.durationMinutes,
        startSoc: event.startSoc,
        endSoc: event.endSoc,
        chargeEnergyConsumed: event.chargeEnergyConsumed,
        dischargeEnergyConsumed: event.dischargeEnergyConsumed,
        distance: event.distance,
        bmsSoh: event.bmsSoh,
        batteryID: event.batteryID,
        deviceID: event.deviceID,
        licensePlateNo: event.licensePlateNo,
        originDepo: event.originDepo,
        originCity: event.originCity,
        originState: event.originState,
        originCountry: event.originCountry,
        createdAt: event.createdAt,
        calculatedChargeEnergy: event.calculatedChargeEnergy,
        dataGapPercent: event.dataGapPercent,
        idealChargingEnergy: event.idealChargingEnergy,
        kwhPerKm: event.kwhPerKm,
        kwhPerSoc: event.kwhPerSoc,
        kmPerSoc: event.kmPerSoc,
        maxTemperature: event.maxTemperature,
        minTemperature: event.minTemperature,
        regenEnergy: event.regenEnergy,
        regenEnergyPercent: event.regenEnergyPercent,
        temperatureDiff: event.temperatureDiff,
        key: `event_${dateGroup.toLowerCase().replace(/\s/g, "_")}_${
          eventIndex + 1
        }`,
      };

      return transformedEvent;
    });

    return {
      event_group: dateGroup,
      key: `group_${dateGroup.toLowerCase().replace(/\s/g, "_")}`,
      children,
    };
  });

  return result;
};
